import React from 'react';
import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  Image,
  Spacer,
  Tag,
  Text,
} from '@chakra-ui/react';
import {useCreateIntentModel} from '../../hooks/api/intent';
import {useCustomer} from '../../hooks/api/metadata';
import {useIntegrations} from '../../hooks/api/integrations';
import {PageHeader} from '../PageHeader';
import {Link, useNavigate} from 'react-router-dom';
import {IntentModel} from '../../shared/customer';
import {ChakraShare} from '../Icons/Carbon';
import {LinkedIn} from '../Icons/LinkedIn';
import {IntegrationResponse} from '../../shared/api/definitions/integration';
import {DelayedSpinner} from '../DelayedSpinner';

export const IntentModels = () => {
  const {intentModels = []} = useCustomer();
  const {data: integrations, isPending: isIntegrationsPending} =
    useIntegrations({});

  if (isIntegrationsPending) {
    return (
      <AbsoluteCenter>
        <DelayedSpinner />
      </AbsoluteCenter>
    );
  }

  return (
    <Flex direction="column" gap="8">
      <PageHeader px={8} title="Intent Models" />
      <Flex direction="column" gap="4" px="8">
        {!intentModels.length ? (
          <EmptyState integrations={integrations ?? []} />
        ) : (
          intentModels.map((intentModel) => (
            <IntentModelRow
              key={intentModel.id.toString()}
              intentModel={intentModel}
            />
          ))
        )}
      </Flex>
    </Flex>
  );
};

const EmptyState = ({integrations}: {integrations: IntegrationResponse[]}) => {
  const navigate = useNavigate();
  const createIntentModel = useCreateIntentModel();

  const hasWebIntent = integrations.some(({type}) => type === 'webIntent');
  const hasLinkedIn = integrations.some(({type}) => type === 'linkedin');
  const hasAnyIntentIntegration = hasWebIntent || hasLinkedIn;

  const createButton = (
    <Button
      colorScheme="kbuttonblue"
      onClick={() =>
        createIntentModel.mutate(
          {},
          {onSuccess: ({id}) => navigate(`/intent/${id}`)}
        )
      }
      isLoading={createIntentModel.isPending}
      mt={2}
    >
      Create Intent Model
    </Button>
  );

  return (
    <Flex direction="column" align="center" pt={16} gap={4}>
      {!hasAnyIntentIntegration ? (
        <>
          <ChakraShare boxSize="48px" color="kgray.300" />
          <Text color="kgray.300">
            Enable Web Intent or connect with LinkedIn to create intent models.
          </Text>
          <Button
            as={Link}
            to="/settings/integrations"
            colorScheme="kbuttonblue"
            variant="link"
          >
            Go to Integrations
          </Button>
        </>
      ) : hasWebIntent ? (
        <>
          <Image src="/web-intent.svg" boxSize="64px" />
          <Text color="kgray.300">
            Intent models are automatically generated when we receive your
            initial Web Intent data.
          </Text>
          <Text color="kgray.300" fontSize="sm">
            You can also create a custom intent model manually.
          </Text>
          {createButton}
        </>
      ) : (
        <>
          <LinkedIn boxSize="48px" color="#0077B7" />
          <Text color="kgray.300">
            Create an intent model to track account engagement from LinkedIn.
          </Text>
          {createButton}
        </>
      )}
    </Flex>
  );
};

const IntentModelRow = ({intentModel}: {intentModel: IntentModel}) => {
  return (
    <Box borderRadius={8} borderColor="kgray.200" borderWidth={1} p={8}>
      <Flex alignItems="center" gap="2">
        <Text fontSize="xl" fontWeight="500">
          {intentModel.label}
        </Text>
        <Tag
          color="kgray.300"
          size="sm"
          fontSize="x-small"
          textTransform="uppercase"
        >
          Active
        </Tag>
        <Spacer />
        <Button
          as={Link}
          colorScheme="kbuttonblue"
          fontSize="sm"
          fontWeight="500"
          to={`/intent/${intentModel.id}`}
          variant="outline"
        >
          Adjust Tiers & Signals
        </Button>
      </Flex>
    </Box>
  );
};
